import {
  Button,
  ButtonProps,
  Spinner,
  Stack,
  TPopover,
  TPopoverContentProps,
  TPopoverProps,
  YGroup,
  isWeb,
  shadowStandard,
} from '@my/ui'
import {
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Circle,
  CircleDot,
  HelpCircle,
  Star,
  XOctagon,
} from '@tamagui/lucide-icons'
import { useUpdateEventRsvp } from './commands'
import { useState } from 'react'
import {
  GroupEventRsvpStatus,
  groupEventRsvpQuery,
  useGroupEventRsvpQuery,
} from '../../queries/use-group-event-rsvp.query'
import { groupEventByIdQuery } from '../../queries'

const DropdownButton = ({ children, iconAfter, loading, ...props }: ButtonProps) => {
  return (
    <Button
      theme="primary4"
      justifyContent="flex-start"
      textAlign="left"
      size="$3"
      px="$2.5"
      focusStyle={{
        outlineWidth: 0,
      }}
      {...props}
    >
      {children}
      <Stack ml="auto">{loading ? <Spinner color="$black" size="small" /> : iconAfter}</Stack>
    </Button>
  )
}

const labelMap: Record<GroupEventRsvpStatus, string> = {
  going: 'Going',
  maybe: 'Interested',
  notGoing: 'Not Going',
}

const iconMap: Record<GroupEventRsvpStatus, JSX.Element> = {
  going: <CheckCircle size={18} />,
  maybe: <HelpCircle size={18} />,
  notGoing: <XOctagon size={18} />,
}

interface GroupEventRsvpDropdownProps {
  eventId: string
  userId: string
  popoverProps?: TPopoverProps
  contentProps?: TPopoverContentProps
  itemProps?: ButtonProps
  triggerProps?: ButtonProps
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const GroupEventRsvpDropdown = (props: GroupEventRsvpDropdownProps) => {
  const { data } = useGroupEventRsvpQuery({
    variables: {
      id: props.eventId,
      userId: props.userId,
    },
    skip: !props.eventId || !props.userId,
    fetchPolicy: 'cache-and-network',
  })

  const currentStatus = data?.GroupEventRSVPs?.docs?.[0]?.status ?? 'notGoing'

  const [updateEventRsvp, { loading }] = useUpdateEventRsvp()

  // Used for showing loading state for appropriate button
  const [action, setAction] = useState<GroupEventRsvpStatus | null>(null)

  const handleUpdateEventRsvp = async (status: GroupEventRsvpStatus) => {
    setAction(status)
    await updateEventRsvp({
      variables: {
        id: props.eventId,
        status,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: groupEventByIdQuery.query,
          variables: {
            id: props.eventId,
          },
        },
        {
          query: groupEventRsvpQuery.query,
          variables: {
            id: props.eventId,
            userId: props.userId,
          },
        },
      ],
    })
    setAction(null)
    props.onOpenChange(false)
  }

  return (
    <TPopover
      open={props.open}
      onOpenChange={props.onOpenChange}
      allowFlip
      placement="bottom-end"
      {...props.popoverProps}
    >
      <TPopover.Anchor flex={1}>
        <DropdownButton
          {...(isWeb ? { w: '100%' } : { flex: 1 })}
          onPress={() => props.onOpenChange(true)}
          iconAfter={props.open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          icon={currentStatus === 'notGoing' ? <Star size={18} /> : iconMap[currentStatus]}
          zIndex="$1"
          {...props.triggerProps}
        >
          {currentStatus === 'notGoing' ? 'RSVP' : labelMap[currentStatus]}
        </DropdownButton>
      </TPopover.Anchor>
      {(props.open || isWeb) && (
        <TPopover.Content
          p="$0"
          bg="$red10"
          w="100%"
          {...shadowStandard}
          enterStyle={{ x: 0, y: -10, opacity: 0 }}
          exitStyle={{ x: 0, y: -10, opacity: 0, pointerEvents: 'none' }}
          x={0}
          y={0}
          o={1}
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          {...props.contentProps}
        >
          <YGroup w="100%" bg="$white">
            <DropdownButton
              icon={iconMap.going}
              onPress={() => handleUpdateEventRsvp('going')}
              iconAfter={currentStatus === 'going' ? <CircleDot size={18} /> : <Circle size={18} />}
              minWidth={200}
              loading={action === 'going' && loading}
            >
              {labelMap.going}
            </DropdownButton>
            <DropdownButton
              icon={iconMap.maybe}
              onPress={() => handleUpdateEventRsvp('maybe')}
              iconAfter={currentStatus === 'maybe' ? <CircleDot size={18} /> : <Circle size={18} />}
              minWidth={200}
              loading={action === 'maybe' && loading}
            >
              {labelMap.maybe}
            </DropdownButton>
            <DropdownButton
              icon={iconMap.notGoing}
              onPress={() => handleUpdateEventRsvp('notGoing')}
              iconAfter={
                currentStatus === 'notGoing' ? <CircleDot size={18} /> : <Circle size={18} />
              }
              minWidth={200}
              loading={action === 'notGoing' && loading}
            >
              {labelMap.notGoing}
            </DropdownButton>
          </YGroup>
        </TPopover.Content>
      )}
    </TPopover>
  )
}
